/**
 * Service for interacting with the local file system via backend API.
 */

import { FileType, FolderType } from '../providers/FileManager';
import axios from 'axios';

import { getHatateToken } from '../helpers/utils';

interface DirectoryResponse {
  path: string;
  folders: FolderType[];
  files: FileType[];
}

// TOKEN
const HatateToken = getHatateToken();
const API_KEY = "test";
// Base URL for the backend API
const API_BASE_URL = 'https://backend.anubisai.net/api';

const FileSystemService = {
  /**
   * Base path for the user files
   */
  basePath: '/tmp/user/files',

  /**
   * Create a new directory
   * @param {string} path - Path where to create the directory
   * @param {string} name - Name of the new directory
   * @returns {Promise<FolderType>} - The created directory object
   */
  async createDirectory(path: string, name: string): Promise<FolderType> {
    try {
      console.log('Creating directory:', { 
        baseUrl: API_BASE_URL,
        path, 
        name,
        token: HatateToken
      });
      
      const response = await axios.post(`${API_BASE_URL}/filesystem/directory`, 
        {
          path: this.getRelativePath(path),
          name: name,
          mlStuff: {
            filesystemPort: '5001'
          }
        }, 
        {
          headers: { 
            'Content-Type': 'application/json',
            'x-api-key': API_KEY,
            'Authorization': `${HatateToken}`
          }
        }
      );
      
      console.log('Directory creation response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error creating directory:', error);
      
      // Log more detailed error information
      if (error.response) {
        console.error('Error response:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      }
      
      throw error;
    }
  },

  /**
   * Read the contents of a directory
   * @param {string} path - The path to read (relative to basePath)
   * @returns {Promise<DirectoryResponse>} - Object containing folders and files
   */
  async readDirectory(path: string): Promise<DirectoryResponse> {
    try {
      console.log('Read Directory Request:', {
        url: `${API_BASE_URL}/filesystem/directory`,
        path,
        mlStuff: {
          filesystemPort: '5001'
        }
      });

      const response = await axios.get(`${API_BASE_URL}/filesystem/directory`, {
        headers: { 
          'x-api-key': API_KEY,
          'Authorization': `${HatateToken}`
        },
        params: { 
          path: this.getRelativePath(path),
          'mlStuff.filesystemPort': '5001'
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error reading directory:', error);
      
      // Log more detailed error information
      if (error.response) {
        console.error('Error response:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      }
      
      throw error;
    }
  },

  /**
   * Create a new file
   * @param {string} path - Directory path where to create the file
   * @param {File} file - The file to upload
   * @returns {Promise<FileType>} - The created file object
   */
  async uploadFile(path: string, file: File): Promise<FileType> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('path', this.getRelativePath(path));
      formData.append('mlStuff.filesystemPort', '5001');
      
      const response = await axios.post(`${API_BASE_URL}/filesystem/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'x-api-key': API_KEY,
          'Authorization': `${HatateToken}`
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error uploading file:', error);
      
      // Log more detailed error information
      if (error.response) {
        console.error('Error response:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      }
      
      throw error;
    }
  },

  /**
   * Get the path relative to the basePath
   * @param {string} path - Path (usually with leading /)
   * @returns {string} - Path relative to basePath
   */
  getRelativePath(path: string): string {
    // Normalize path
    path = path || '/';
    
    // Asegurarse de que no hay barras dobles
    while (path.includes('//')) {
      path = path.replace('//', '/');
    }
    
    return path;
  },

  /**
   * Delete a file
   * @param {string} path - Path to the file to delete
   * @returns {Promise<boolean>} - Success indicator
   */
  async deleteFile(path: string): Promise<boolean> {
    try {
      console.log('Deleting file at path:', path);
      
      // Normalizar la ruta y asegurarse de que sea correcta
      const relativePath = this.getRelativePath(path);
      console.log('Normalized path for file deletion:', relativePath);
      
      // Verificación adicional para depuración
      if (!relativePath) {
        throw new Error('File path cannot be empty');
      }
      
      // Configurar la petición con los parámetros correctos
      const config = {
        headers: { 
          'x-api-key': API_KEY,
          'Authorization': `${HatateToken}`,
          'Content-Type': 'application/json'
        },
        params: {
          path: relativePath,
          'mlStuff.filesystemPort': '5001'
        }
      };
      
      console.log('DELETE file request configuration:', JSON.stringify(config, null, 2));
      
      // Realizar la petición DELETE
      const response = await axios.delete(`${API_BASE_URL}/filesystem/file`, config);
      
      console.log('File deletion successful. Response:', response.data);
      return true;
    } catch (error: any) {
      console.error('Error deleting file:', error);
      
      // Log más detallado de errores
      if (error.response) {
        console.error('Error response from server:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        });
      } else if (error.request) {
        console.error('No response received from server. Request:', error.request);
      } else {
        console.error('Error message:', error.message);
        console.error('Error config:', error.config);
      }
      
      throw error;
    }
  },

  /**
   * Delete a directory
   * @param {string} path - Path to the directory to delete
   * @returns {Promise<boolean>} - Success indicator
   */
  async deleteDirectory(path: string): Promise<boolean> {
    try {
      console.log('Deleting directory at path:', path);
      
      // Normalizar la ruta y asegurarse de que sea correcta
      const relativePath = this.getRelativePath(path);
      console.log('Normalized path for directory deletion:', relativePath);
      
      // Verificación adicional para depuración
      if (!relativePath) {
        throw new Error('Directory path cannot be empty');
      }
      
      // Configurar la petición con los parámetros correctos
      const config = {
        headers: { 
          'x-api-key': API_KEY,
          'Authorization': `${HatateToken}`,
          'Content-Type': 'application/json'
        },
        params: {
          path: relativePath,
          'mlStuff.filesystemPort': '5001'
        }
      };
      
      console.log('DELETE directory request configuration:', JSON.stringify(config, null, 2));
      
      // Realizar la petición DELETE
      const response = await axios.delete(`${API_BASE_URL}/filesystem/directory`, config);
      
      console.log('Directory deletion successful. Response:', response.data);
      return true;
    } catch (error: any) {
      console.error('Error deleting directory:', error);
      
      // Log más detallado de errores
      if (error.response) {
        console.error('Error response from server:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        });
      } else if (error.request) {
        console.error('No response received from server. Request:', error.request);
      } else {
        console.error('Error message:', error.message);
        console.error('Error config:', error.config);
      }
      
      throw error;
    }
  },

  /**
   * Rename a file
   * @param {string} path - Path to the file
   * @param {string} newName - New name for the file
   * @returns {Promise<FileType>} - Updated file object
   */
  async renameFile(path: string, newName: string): Promise<FileType> {
    try {
      const response = await axios.put(`${API_BASE_URL}/filesystem/file/rename`, {
        path: this.getRelativePath(path),
        newName,
        mlStuff: {
          filesystemPort: '5001'
        }
      }, {
        headers: { 
          'x-api-key': API_KEY,
          'Authorization': `${HatateToken}`
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error renaming file:', error);
      
      // Log more detailed error information
      if (error.response) {
        console.error('Error response:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      }
      
      throw error;
    }
  }
};

export default FileSystemService;